import { isEmpty } from 'lodash'

import { sentryLogger } from 'sentry'

import { makeRequestUrl, modifyQueries } from '../app/helpers'
import { get, post } from '../request'

// Vite's `define` shim only replaces dot-access `process.env.FOO` at
// build time — bracket access `process.env[name]` is left as a runtime
// read against an empty object, so the original validation loop fired
// "not set" warnings even when the dot-access values were injected fine.
// Switched to per-key dot-access checks so the validation matches what
// the compiler actually substituted.
export const apiURLOrigin = process.env.REACT_APP_API_BASE_URL
export const apiURLOriginV2 = process.env.REACT_APP_API_BASE_URL_V2
export const apiURLOriginV4 = process.env.REACT_APP_API_BASE_URL_V4

if (!apiURLOrigin)
  console.error('REACT_APP_API_BASE_URL is not set in environment')
if (!apiURLOriginV2)
  console.error('REACT_APP_API_BASE_URL_V2 is not set in environment')
if (!apiURLOriginV4)
  console.error('REACT_APP_API_BASE_URL_V4 is not set in environment')

export const apiEndpoints = {
  auth: `${apiURLOrigin}/auth`,
  preschoolUpdatesAudit: `${apiURLOrigin}/oh_moments/audit/preschool_updates`,
  kekaAttendanceWeek: `${apiURLOrigin}/oh_classes/alfred/keka_attendance/week`,
  kekaPunctuality: `${apiURLOrigin}/oh_classes/alfred/keka_attendance/punctuality`,
  punctualityNotices: `${apiURLOrigin}/oh_classes/alfred/keka_attendance/punctuality_notices`,
  teacherScorecard: `${apiURLOrigin}/oh_classes/alfred/teacher_scorecard`,
  preschoolUpdateTrends: `${apiURLOrigin}/oh_moments/audit/preschool_update_trends`,
  preschoolCentreTrends: `${apiURLOrigin}/oh_moments/audit/preschool_centre_trends`,
  addTribe: `${apiURLOrigin}/oh_clubs/add_tribe_member`,
  batch: `${apiURLOrigin}/oh_classes/batch`,
  fetchBatch: `${apiURLOrigin}/oh_classes/batch/alfred`,
  summerCampBatch: `${apiURLOrigin}/oh_classes/batch/alfred/summer_camp`,
  preschoolBatch: `${apiURLOrigin}/oh_classes/batch/alfred/preschool`,
  preschoolBatchOptions: `${apiURLOrigin}/oh_classes/batch/alfred/preschool/options`,
  board: `${apiURLOrigin}/commons/board`,
  bruceChat: `${apiURLOrigin}/oh_assistant/alfred/assistant/chat`,
  bruceConfig: `${apiURLOrigin}/oh_assistant/alfred/assistant/config`,
  bruceFeedback: `${apiURLOrigin}/oh_assistant/alfred/assistant/feedback`,
  bruceHistory: `${apiURLOrigin}/oh_assistant/alfred/assistant/history`,
  bruceAgents: `${apiURLOrigin}/oh_assistant/alfred/assistant/agents`,
  bruceAccess: `${apiURLOrigin}/oh_assistant/alfred/assistant/access`,
  bruceAccessSearch: `${apiURLOrigin}/oh_assistant/alfred/assistant/access/search`,
  bruceActions: `${apiURLOrigin}/oh_assistant/alfred/assistant/actions`,
  bruceReminders: `${apiURLOrigin}/oh_assistant/alfred/assistant/reminders`,
  bruceConversations: `${apiURLOrigin}/oh_assistant/alfred/assistant/conversations`,
  centre: `${apiURLOrigin}/commons/centre`,
  procurementBudget: `${apiURLOrigin}/oh_procurement/alfred/budget`,
  procurementBudgetTransfer: `${apiURLOrigin}/oh_procurement/alfred/budget/transfer`,
  procurementMyCentres: `${apiURLOrigin}/oh_procurement/alfred/my-centres`,
  procurementPettyCash: `${apiURLOrigin}/oh_procurement/alfred/petty-cash`,
  procurementPettyCashBatch: `${apiURLOrigin}/oh_procurement/alfred/petty-cash/batch`,
  procurementPettyCashDraft: `${apiURLOrigin}/oh_procurement/alfred/petty-cash/draft`,
  procurementPettyCashSummary: `${apiURLOrigin}/oh_procurement/alfred/petty-cash/summary`,
  procurementConsumablesRoster: `${apiURLOrigin}/oh_procurement/alfred/consumables/roster`,
  procurementConsumablesHistory: `${apiURLOrigin}/oh_procurement/alfred/consumables/history`,
  procurementConsumablesAdminAction: `${apiURLOrigin}/oh_procurement/alfred/consumables/admin-action`,
  procurementConsumablesPending: `${apiURLOrigin}/oh_procurement/alfred/consumables/pending`,
  procurementConsumablesDecision: `${apiURLOrigin}/oh_procurement/alfred/consumables/decision`,
  procurementConsumablesReceipt: `${apiURLOrigin}/oh_procurement/alfred/consumables/receipt`,
  procurementConsumablesCentreSetting: `${apiURLOrigin}/oh_procurement/alfred/consumables/centre-setting`,
  procurementProgramMaterial: `${apiURLOrigin}/oh_procurement/alfred/program-material`,
  procurementProgramMaterialHistory: `${apiURLOrigin}/oh_procurement/alfred/program-material/history`,
  procurementProgramMaterialReceipt: `${apiURLOrigin}/oh_procurement/alfred/program-material/receipt`,
  procurementOrderLineStatus: `${apiURLOrigin}/oh_procurement/alfred/program-material/line-status`,
  procurementOrderLineDecision: `${apiURLOrigin}/oh_procurement/alfred/program-material/line-decision`,
  procurementOrderAdminAction: `${apiURLOrigin}/oh_procurement/alfred/program-material/admin-action`,
  procurementOrderFeedback: `${apiURLOrigin}/oh_procurement/alfred/program-material/feedback`,
  procurementPettyCashAllocation: `${apiURLOrigin}/oh_procurement/alfred/petty-cash/allocation`,
  procurementPettyCashOverview: `${apiURLOrigin}/oh_procurement/alfred/petty-cash/overview`,
  procurementPettyCashAcknowledge: `${apiURLOrigin}/oh_procurement/alfred/petty-cash/acknowledge`,
  procurementPettyCashFreeze: `${apiURLOrigin}/oh_procurement/alfred/petty-cash/freeze`,
  procurementPettyCashUnlock: `${apiURLOrigin}/oh_procurement/alfred/petty-cash/unlock`,
  procurementRmRegister: `${apiURLOrigin}/oh_procurement/alfred/rm-register`,
  procurementRmReco: `${apiURLOrigin}/oh_procurement/alfred/rm-reco`,
  procurementRmApprovalAccess: `${apiURLOrigin}/oh_procurement/alfred/rm-approval/access`,
  aptProcMyRole: `${apiURLOrigin}/oh_procurement/alfred/apartment/my-role`,
  aptProcKits: `${apiURLOrigin}/oh_procurement/alfred/apartment/kits`,
  aptProcCatalogue: `${apiURLOrigin}/oh_procurement/alfred/apartment/catalogue`,
  aptProcCatalogueAction: `${apiURLOrigin}/oh_procurement/alfred/apartment/catalogue/action`,
  aptProcCategories: `${apiURLOrigin}/oh_procurement/alfred/apartment/categories`,
  aptProcVendors: `${apiURLOrigin}/oh_procurement/alfred/apartment/vendors`,
  aptProcVendorAction: `${apiURLOrigin}/oh_procurement/alfred/apartment/vendors/action`,
  aptProcBudget: `${apiURLOrigin}/oh_procurement/alfred/apartment/budget`,
  aptProcOverview: `${apiURLOrigin}/oh_procurement/alfred/apartment/overview`,
  aptProcLifecycle: `${apiURLOrigin}/oh_procurement/alfred/apartment/lifecycle`,
  aptDailyImportTabs: `${apiURLOrigin}/oh_procurement/alfred/apartment/daily-import/tabs`,
  aptDailyImportPreview: `${apiURLOrigin}/oh_procurement/alfred/apartment/daily-import/preview`,
  aptDailyImportCommit: `${apiURLOrigin}/oh_procurement/alfred/apartment/daily-import/commit`,
  aptDailyDeleteAll: `${apiURLOrigin}/oh_procurement/alfred/apartment/daily/delete-all`,
  aptDailyRestore: `${apiURLOrigin}/oh_procurement/alfred/apartment/daily/restore`,
  aptDailyPushToSheet: `${apiURLOrigin}/oh_procurement/alfred/apartment/daily/push-to-sheet`,
  aptProcRequests: `${apiURLOrigin}/oh_procurement/alfred/apartment/requests`,
  aptProcRequestAction: `${apiURLOrigin}/oh_procurement/alfred/apartment/requests/action`,
  aptProcRequestApproval: `${apiURLOrigin}/oh_procurement/alfred/apartment/requests/approval`,
  aptProcGenerateKit: `${apiURLOrigin}/oh_procurement/alfred/apartment/requests/generate-kit`,
  aptProcGenerateCategory: `${apiURLOrigin}/oh_procurement/alfred/apartment/requests/generate-category`,
  aptProcLine: `${apiURLOrigin}/oh_procurement/alfred/apartment/requests/line`,
  aptProcLineAdd: `${apiURLOrigin}/oh_procurement/alfred/apartment/requests/line/add`,
  aptProcLineReorder: `${apiURLOrigin}/oh_procurement/alfred/apartment/requests/line/reorder`,
  aptProcBulkAddCategory: `${apiURLOrigin}/oh_procurement/alfred/apartment/requests/bulk-add-category`,
  aptProcInventory: `${apiURLOrigin}/oh_procurement/alfred/apartment/inventory`,
  aptProcInventoryAllocate: `${apiURLOrigin}/oh_procurement/alfred/apartment/inventory/allocate`,
  aptProcInventoryConsume: `${apiURLOrigin}/oh_procurement/alfred/apartment/inventory/consume`,
  aptProcInventoryDeleteItem: `${apiURLOrigin}/oh_procurement/alfred/apartment/inventory/delete-item`,
  aptProcInventoryCount: `${apiURLOrigin}/oh_procurement/alfred/apartment/inventory/count`,
  procurementCycleWindow: `${apiURLOrigin}/oh_procurement/alfred/cycle-window`,
  afterschoolOrder: `${apiURLOrigin}/oh_procurement/alfred/afterschool/order`,
  afterschoolSave: `${apiURLOrigin}/oh_procurement/alfred/afterschool/save`,
  afterschoolPending: `${apiURLOrigin}/oh_procurement/alfred/afterschool/pending`,
  afterschoolDecision: `${apiURLOrigin}/oh_procurement/alfred/afterschool/decision`,
  afterschoolCatalogue: `${apiURLOrigin}/oh_procurement/alfred/afterschool/catalogue`,
  afterschoolCatalogueSave: `${apiURLOrigin}/oh_procurement/alfred/afterschool/catalogue/save`,
  capexListItems: `${apiURLOrigin}/oh_procurement/alfred/capex/list-items`,
  capexBudgets: `${apiURLOrigin}/oh_procurement/alfred/capex/budgets`,
  capexSheet: `${apiURLOrigin}/oh_procurement/alfred/capex/sheet`,
  capexSave: `${apiURLOrigin}/oh_procurement/alfred/capex/save`,
  capexPending: `${apiURLOrigin}/oh_procurement/alfred/capex/pending`,
  capexBoard: `${apiURLOrigin}/oh_procurement/alfred/capex/board`,
  capexDecision: `${apiURLOrigin}/oh_procurement/alfred/capex/decision`,
  capexReceipt: `${apiURLOrigin}/oh_procurement/alfred/capex/receipt`,
  procurementCatalogue: `${apiURLOrigin}/oh_procurement/alfred/catalogue`,
  procurementCatalogueBrowse: `${apiURLOrigin}/oh_procurement/alfred/catalogue/browse`,
  procurementCatalogueEdit: `${apiURLOrigin}/oh_procurement/alfred/catalogue/edit`,
  procurementDashboard: `${apiURLOrigin}/oh_procurement/alfred/dashboard`,
  procurementOrders: `${apiURLOrigin}/oh_procurement/alfred/orders`,
  procurementHqMonthlyBoard: `${apiURLOrigin}/oh_procurement/alfred/hq/monthly-board`,
  procurementHqMonthlyBoardDetail: `${apiURLOrigin}/oh_procurement/alfred/hq/monthly-board/detail`,
  procurementHqBoardExport: `${apiURLOrigin}/oh_procurement/alfred/hq/monthly-board/export`,
  procurementConsumableItem: `${apiURLOrigin}/oh_procurement/alfred/consumable-item`,
  procurementHqOrderTimeline: `${apiURLOrigin}/oh_procurement/alfred/hq/order/timeline`,
  procurementHqOrderAction: `${apiURLOrigin}/oh_procurement/alfred/hq/order/action`,
  procurementReminderControls: `${apiURLOrigin}/oh_procurement/alfred/hq/reminder-controls`,
  procurementReminderTestSend: `${apiURLOrigin}/oh_procurement/alfred/hq/reminder-controls/test-send`,
  procurementReminderExplain: `${apiURLOrigin}/oh_procurement/alfred/hq/reminder-controls/explain`,
  procurementReminderAudit: `${apiURLOrigin}/oh_procurement/alfred/hq/reminder-controls/audit`,
  procurementReminderPlan: `${apiURLOrigin}/oh_procurement/alfred/hq/reminder-controls/plan`,
  procurementReminderCycle: `${apiURLOrigin}/oh_procurement/alfred/hq/reminder-controls/cycle`,
  procurementReminderSent: `${apiURLOrigin}/oh_procurement/alfred/hq/reminder-controls/sent`,
  procurementReminderRecall: `${apiURLOrigin}/oh_procurement/alfred/hq/reminder-controls/recall`,
  procurementOrderDecision: `${apiURLOrigin}/oh_procurement/alfred/orders/decision`,
  procurementPmAttachment: `${apiURLOrigin}/oh_procurement/alfred/program-material/attachment`,
  procurementPmItemImage: `${apiURLOrigin}/oh_procurement/alfred/program-material/item-image`,
  procurementAttachmentPresign: `${apiURLOrigin}/oh_procurement/alfred/attachments/presign`,
  procurementAttachmentUpload: `${apiURLOrigin}/oh_procurement/alfred/attachments/upload`,
  procurementAttachments: `${apiURLOrigin}/oh_procurement/alfred/attachments`,
  procurementNotifications: `${apiURLOrigin}/oh_procurement/alfred/notifications`,
  procurementSop: `${apiURLOrigin}/oh_procurement/alfred/sop`,
  // Cash Register (oh_cash_register) — cash/cheque collections in hand at
  // centres and the bank deposits (with challans) that clear them.
  cashRegisterLedger: `${apiURLOrigin}/cash_register/alfred/ledger`,
  cashRegisterReceived: `${apiURLOrigin}/cash_register/alfred/received`,
  cashRegisterDeposits: `${apiURLOrigin}/cash_register/alfred/deposits`,
  cashRegisterHqRollup: `${apiURLOrigin}/cash_register/alfred/hq/rollup`,
  cashRegisterHqDeposits: `${apiURLOrigin}/cash_register/alfred/hq/deposits`,
  // HQ Finance's own drawer: envelopes on their way in, envelopes held, and
  // the bank runs that empty it. Distinct from cashRegisterHqDeposits above,
  // which lists deposits made BY the centres.
  cashRegisterHqSafe: `${apiURLOrigin}/cash_register/alfred/hq/safe`,
  cashRegisterHqReceive: `${apiURLOrigin}/cash_register/alfred/hq/receive`,
  cashRegisterHqSafeDeposits: `${apiURLOrigin}/cash_register/alfred/hq/safe/deposits`,
  course: `${apiURLOrigin}/oh_classes/course`,
  // Preschool curriculum (Phase 5) — admin CRUD against the
  // oh_preschool_evaluation Django app shipped in Phase 1.
  preschoolSkill: `${apiURLOrigin}/preschool-evaluation/admin/skills`,
  preschoolWeeklyTheme: `${apiURLOrigin}/preschool-evaluation/admin/weekly-themes`,
  preschoolWeeklySkillPlan: `${apiURLOrigin}/preschool-evaluation/admin/weekly-skill-plans`,
  preschoolMonthlyTheme: `${apiURLOrigin}/preschool-evaluation/admin/monthly-themes`,
  preschoolAnnualLo: `${apiURLOrigin}/preschool-evaluation/admin/annual-los`,
  preschoolSpiralLo: `${apiURLOrigin}/preschool-evaluation/admin/spiral-los`,
  // Teacher authoring endpoints (the same ones the mobile teacher app calls).
  // Trailing slashes are required — the PUTs would 404 on Django APPEND_SLASH.
  preschoolTeacherWeeklyBundle: `${apiURLOrigin}/preschool-evaluation/teacher/weekly-bundle/`,
  preschoolAdminWeeklyBundle: `${apiURLOrigin}/preschool-evaluation/admin/weekly-bundle/`,
  preschoolAdminEvaluationPhoto: `${apiURLOrigin}/preschool-evaluation/admin/weekly-evaluation/photo/`,
  preschoolTeacherWeeklyEvaluation: `${apiURLOrigin}/preschool-evaluation/teacher/weekly-evaluation/`,
  preschoolTeacherTermReport: `${apiURLOrigin}/preschool-evaluation/teacher/term-report/`,
  preschoolTeacherTermProgress: `${apiURLOrigin}/preschool-evaluation/teacher/term-progress/`,
  preschoolAdminTermPhoto: `${apiURLOrigin}/preschool-evaluation/admin/term-progress/photo/`,
  // Lesson Planner (EarlyLearn) — admin CRUD against oh_lesson_planner.
  plannerGradeGroup: `${apiURLOrigin}/oh_lesson_planner/admin/grade-groups`,
  plannerSubject: `${apiURLOrigin}/oh_lesson_planner/admin/subjects`,
  plannerLayout: `${apiURLOrigin}/oh_lesson_planner/admin/layouts`,
  plannerGradeSubject: `${apiURLOrigin}/oh_lesson_planner/admin/grade-subjects`,
  plannerQuota: `${apiURLOrigin}/oh_lesson_planner/admin/quotas`,
  plannerActivity: `${apiURLOrigin}/oh_lesson_planner/admin/activities`,
  plannerTimetable: `${apiURLOrigin}/oh_lesson_planner/admin/timetables`,
  plannerInventory: `${apiURLOrigin}/oh_lesson_planner/admin/inventory`,
  plannerObservation: `${apiURLOrigin}/oh_lesson_planner/admin/observations`,
  plannerCsvImport: `${apiURLOrigin}/oh_lesson_planner/admin/csv-import`,
  plannerPlans: `${apiURLOrigin}/oh_lesson_planner/admin/plans`,
  plannerPlanDetail: `${apiURLOrigin}/oh_lesson_planner/admin/plans/detail`,
  plannerPlanTeachers: `${apiURLOrigin}/oh_lesson_planner/admin/plan-teachers`,
  plannerObservationProgram: `${apiURLOrigin}/oh_lesson_planner/admin/observation-programs`,
  plannerObservationQuestion: `${apiURLOrigin}/oh_lesson_planner/admin/observation-questions`,
  plannerProgramObservation: `${apiURLOrigin}/oh_lesson_planner/admin/program-observations`,
  plannerObservationTeachers: `${apiURLOrigin}/oh_lesson_planner/admin/observation-teachers`,
  plannerObservationSessions: `${apiURLOrigin}/oh_lesson_planner/admin/observation-sessions`,
  plannerAllObservations: `${apiURLOrigin}/oh_lesson_planner/admin/all-observations`,
  plannerQotQuestion: `${apiURLOrigin}/oh_lesson_planner/admin/qot-questions`,
  plannerPlanEditor: `${apiURLOrigin}/oh_lesson_planner/admin/plan-editor`,
  plannerTimetableClaim: `${apiURLOrigin}/oh_lesson_planner/admin/plan-timetable-claim`,
  plannerPlanRepository: `${apiURLOrigin}/oh_lesson_planner/admin/plan-repository`,
  plannerPlanCell: `${apiURLOrigin}/oh_lesson_planner/admin/plan-cell`,
  plannerPlanStatus: `${apiURLOrigin}/oh_lesson_planner/admin/plan-status`,
  cloudFileStorage: 'https://api.cloudinary.com/v1_1',
  club: `${apiURLOrigin}/oh_clubs/club`,
  clubRegistrations: `${apiURLOrigin}/oh_clubs/club/registration`,
  clubRegistrationConvert: `${apiURLOrigin}/oh_clubs/club/registration/convert`,
  tribeList: `${apiURLOrigin}/oh_clubs/tribe/club`,
  enrolment: `${apiURLOrigin}/oh_classes/enrolment`,
  trialSessions: `${apiURLOrigin}/oh_classes/attendance`,
  fetchEnrolment: `${apiURLOrigin}/oh_classes/enrolment`,
  event: `${apiURLOrigin}/oh_events`,
  growth: `${apiURLOrigin}/oh_growth`,
  ohClasses: `${apiURLOrigin}/oh_classes`,
  payment: `${apiURLOrigin}/oh_payments`,
  emandateStatus: `${apiURLOrigin}/oh_payments/razorpay_emandate/status`,
  referralCode: `${apiURLOrigin}/oh_clubs/referral_code`,
  room: `${apiURLOrigin}/commons/room`,
  oh_schedules: `${apiURLOrigin}/oh_schedules`,
  schedule: `${apiURLOrigin}/oh_schedules/schedule`,
  standard: `${apiURLOrigin}/commons/standard`,
  school: `${apiURLOrigin}/commons/school`,
  subject: `${apiURLOrigin}/commons/subject`,
  subjectCategory: `${apiURLOrigin}/commons/subject_category`,
  configApp: `${apiURLOrigin}/config/app`,
  thirdparty: `${apiURLOrigin}/thirdparty`,
  teacher: `${apiURLOrigin}/oh_users/teacher`,
  tribe: `${apiURLOrigin}/oh_clubs/tribe`,
  user: `${apiURLOrigin}/oh_users`,
  journeyMentor: `${apiURLOrigin}/oh_journey_mentors`,
  common: `${apiURLOrigin}`,
  communication: `${apiURLOrigin}/oh_communications/communication`,
  createSibling: `${apiURLOrigin}/base/user`,
  student: `${apiURLOrigin}/oh_users/student`,
  parentGuardian: `${apiURLOrigin}/oh_users/parent_guardian`,
  parentMergePreview: `${apiURLOrigin}/oh_users/parent/merge/preview/`,
  parentMerge: `${apiURLOrigin}/oh_users/parent/merge/`,
  parentMergeRequestAdmin: `${apiURLOrigin}/oh_users/parent/merge/request/admin/`,
  studentMergePreview: `${apiURLOrigin}/oh_users/student/merge/preview/`,
  studentMerge: `${apiURLOrigin}/oh_users/student/merge/`,
  createStudent: `${apiURLOrigin}/oh_users/create_student`,
  getStudentParent: `${apiURLOrigin}/oh_users/get_student_parent`,
  validateReferral: `${apiURLOrigin}/oh_users/parent/referral/validate`,
  waitlist: `${apiURLOrigin}/oh_clubs/waitlist`,
  website: `${apiURLOrigin}/oh_www`,
  mockTest: `${apiURLOrigin}/oh_mock_tests`,
  updateNotes: `${apiURLOrigin}/oh_clubs/tribe/membership/notes`,
  waitlistNotes: `${apiURLOrigin}/oh_clubs/waitlist/queue/notes`,
  // Feature 4 — current user's profile (incl. can_apply_custom_discount).
  me: `${apiURLOrigin}/base/me`,

  // Roles & Permissions (HQ). No delete endpoint exists — roles are never
  // deletable, in Alfred or in the DB.
  role: `${apiURLOrigin}/base/role`,
  // Lean list of everyone who can hold a role (admins + teachers), purpose-built
  // for HQ -> Roles & Permissions. Do NOT rebuild it from the /oh_users/* CRUD
  // lists: those are 28 fields per teacher (642KB/500 rows) and, at limit=500,
  // silently dropped 627 of the 1,127 teachers.
  roleHolders: `${apiURLOrigin}/base/role_holders`,
  // Feature 4 — curated discount catalog list. Replaces the AppConfig
  // PRESCHOOL_ADDITIONAL_DISCOUNTS feed (now dead).
  curatedDiscounts: `${apiURLOrigin}/commons/discounts/catalog/`,
  constants: `${apiURLOrigin}/base/constants`,
  geography: `${apiURLOrigin}/commons/geography`,
  centreSchedules: `${apiURLOrigin}/oh_schedules/centre/schedule`,
  modules: `${apiURLOrigin}/oh_classes/course/module`,
  mindsets: `${apiURLOrigin}/oh_classes/course/mindset`,
  parent: `${apiURLOrigin}/oh_users/parent`,
  adminUser: `${apiURLOrigin}/oh_users/admin_user`,
  attendance: `${apiURLOrigin}/oh_classes/attendance/`,
  attendanceV2: `${apiURLOriginV2}/oh_classes/attendance/`,
  dashboardSchedules: `${apiURLOriginV4}/oh_schedules/schedule`,
  feedback: `${apiURLOrigin}/oh_feedback/feedback/`,
  batch_suggestion: `${apiURLOrigin}/oh_classes/course/batch-suggestion`,
  make_up_session: `${apiURLOrigin}/oh_schedules/make_up_session_suggestions`,
  moments: `${apiURLOriginV2}/oh_moments/moment`,
  momentConsumer: `${apiURLOriginV2}/oh_moments/moment_consumer`,
  cart: `${apiURLOrigin}/oh_cart`,
  actions: `${apiURLOrigin}/oh_actions/actions`,
  // NOT `teacher` — that key already means the Users teacher LIST above
  // (/oh_users/teacher). This is the oh_teachers app, which mounts only
  // <uuid>/dashboard and <uuid>/students. Sharing one key made the later
  // literal win, so every fetchTeachers list call 404'd for months while
  // the dashboard kept working — the duplicate was silent in JS.
  teacherDashboard: `${apiURLOrigin}/oh_teachers`,
  programs: `${apiURLOrigin}/oh_programs`,
  programEnrolment: `${apiURLOrigin}/oh_programs/enrolments`,
  googlePlaces: `${apiURLOrigin}/thirdparty/google_places`,
  scheduleFeedback: `${apiURLOrigin}/oh_feedback/schedule_feedback`,
  openbook: `${apiURLOrigin}/oh_openbook/post`,
  openbookFiles: `${apiURLOrigin}/oh_files/file`,
  // Operations — centre maintenance checklists (oh_operations Django app).
  checklistTemplates: `${apiURLOrigin}/oh_operations/checklist/templates`,
  checklistBoard: `${apiURLOrigin}/oh_operations/checklist/board`,
  checklistHistory: `${apiURLOrigin}/oh_operations/checklist/history`,
  checklistRuns: `${apiURLOrigin}/oh_operations/checklist/runs`,
  checklistRunItems: `${apiURLOrigin}/oh_operations/checklist/run-items`,
  checklistSetup: `${apiURLOrigin}/oh_operations/checklist/setup`,
  // Operations — Life Catch-Ups (oh_operations Django app).
  // Centre Operations > Reports — one segment per call.
  centreOpsReport: `${apiURLOrigin}/oh_operations/centre_ops_report`,
  lifeCatchUps: `${apiURLOrigin}/oh_operations/life_catch_up`,
  centreVisitAudits: `${apiURLOrigin}/oh_operations/centre_audit`,
  hkAudits: `${apiURLOrigin}/oh_operations/hk_audit`,
  lifeCatchUpPeople: `${apiURLOrigin}/oh_operations/life_catch_up/people`,
  // Operations — Parent Onboarding tracker (oh_operations Django app).
  parentOnboarding: `${apiURLOrigin}/oh_operations/parent_onboarding`,
  // Revenue Goals — monthly target vs committed (oh_revenue_goals Django app).
  revenueGoals: `${apiURLOrigin}/oh_revenue_goals/goal`,
  // Interaction Audit — non-member prospect interaction coverage.
  interactionAudit: `${apiURLOrigin}/interaction_audit`,
  // Issue Tracker (oh_issues). `/public/<token>` underneath is deliberately
  // unauthenticated — the token identifies the centre.
  issues: `${apiURLOrigin}/issues`,
  // Auto-booking review board (Centre Analytics -> Auto-Booking).
  autoBooking: `${apiURLOrigin}/autobooking`,
  incidentReport: `${apiURLOrigin}/oh_operations/incident_report`,
  // The centres the logged-in user may pick from on Centre Operations pages.
  // Centre-scoped server-side — do NOT substitute /commons/centre here.
  operationsMyCentres: `${apiURLOrigin}/oh_operations/my_centres/alfred`,
  underageConsent: `${apiURLOrigin}/oh_operations/underage_consent`,
  // Field Trips (oh_operations Django app).
  fieldTrips: `${apiURLOrigin}/oh_operations/field_trip`,
  // Operations — Newsletter drafts (oh_operations Django app).
  newsletterDrafts: `${apiURLOrigin}/oh_operations/newsletter/drafts`,
  // Operations — Event Creative AI artwork generation.
  eventCreativeImage: `${apiURLOrigin}/oh_operations/event_creative/generate_image`,
  eventCreativeFaceCrop: `${apiURLOrigin}/oh_operations/event_creative/face_crop`,
  // Operations — Team Wall posters (oh_operations Django app).
  newJoiners: `${apiURLOrigin}/oh_operations/new_joiners`,
  // Centre Operations -> Annual Calendar (HQ edits it).
  annualCalendar: `${apiURLOrigin}/oh_operations/annual_calendar`,
  teamWallDrafts: `${apiURLOrigin}/oh_operations/team_wall/drafts`,
  teamWallImport: `${apiURLOrigin}/oh_operations/team_wall/import_members`,
  teamWallIdFormImport: `${apiURLOrigin}/oh_operations/team_wall/import_id_form`,
  teamWallIdPhoto: `${apiURLOrigin}/oh_operations/team_wall/import_id_photo`,
  newsletterHqSummary: `${apiURLOrigin}/oh_operations/newsletter/hq_summary`,
  teamWallHqMembers: `${apiURLOrigin}/oh_operations/team_wall/hq_members`,
  teamWallKekaShifts: `${apiURLOrigin}/oh_operations/team_wall/keka_shifts`,
  // ACADS — Kolkata tuition (oh_acads Django app).
  acads: `${apiURLOrigin}/oh_acads`,
  tickets: `${apiURLOrigin}/crm/ticket`,
  certifications: `${apiURLOrigin}/commons/certification`,
  transaction: `${apiURLOrigin}/oh_payments/transaction`,
  cartV2: `${apiURLOriginV2}/oh_payments/cart/`,
  coinCalculation: `${apiURLOrigin}/oh_bff/ma/coin_calculation/`,
  orders: `${apiURLOriginV2}/oh_payments/invoice`,
  teacherList: `${apiURLOrigin}/oh_chat/alfred/teachers/`,
  studentList: `${apiURLOrigin}/oh_chat/alfred/chats`,
  chatList: `${apiURLOrigin}/oh_chat/alfred/messages`,
  parentList: `${apiURLOrigin}/oh_chat/alfred/parents`,
  chatChannel: `${apiURLOrigin}/oh_chat/channel`,
  analytics: `${apiURLOrigin}/analytics`,
  // Parent CSAT survey responses (HQ -> CSAT 2026). The submit endpoint that
  // feeds this table is open and lives at the same base; these two are the
  // authenticated Alfred read surface.
  csatResponses: `${apiURLOrigin}/oh_feedback/csat_response/alfred/`,
  csatSummary: `${apiURLOrigin}/oh_feedback/csat_response/alfred/summary/`,
  csatFilterOptions: `${apiURLOrigin}/oh_feedback/csat_response/alfred/filters/`,
  apartments: `${apiURLOrigin}/oh_apartments`,
  // At-Home (1:1 classes at the child's home) — unrelated to At Apartment.
  atHome: `${apiURLOrigin}/oh_at_home`,
  unreadCount: `${apiURLOrigin}/oh_chat/alfred/unread_count/`,
  preschoolProgramSummary: `${apiURLOrigin}/oh_bff/ma/pre_school/payment_summary`,
  psacenrolments: `${apiURLOrigin}/oh_users/student_admission/`,
  preschoolAcademicYears: `${apiURLOrigin}/commons/academic_year/`,
  announcements: `${apiURLOrigin}/commons/announcements`,

  // The membership / journey / interaction endpoints below were removed by the
  // kill of Money + Interaction History + Journey + Superprovider admin
  // surfaces (commits 3f81ede + ce5c8e7). The kill commit's message says
  // "Phase 2 will fold helpers into call sites" — until that Phase 2 ships,
  // ParentDashboard helpers and api/parentMembership.js, api/journey.js still
  // make live calls to these. Restored here so those calls resolve to real
  // URLs instead of `/home/undefinedcancel/` (which 404s and then the post-
  // login dashboard render aborts mid-tree).
  memberships: `${apiURLOrigin}/oh_memberships`,
  plans: `${apiURLOrigin}/oh_memberships/plans/`,
  active_plans: `${apiURLOrigin}/oh_memberships/plans/active`,
  subscriptions: `${apiURLOrigin}/oh_memberships/subscriptions/`,
  addCoins: `${apiURLOrigin}/oh_memberships/topup/`,
  rechargePlans: `${apiURLOrigin}/oh_memberships/recharge_plans/`,
  extendWalletValidity: `${apiURLOrigin}/oh_memberships/coin_summary/extend_validity/`,
  journey: `${apiURLOrigin}/oh_journey/journey/`,
  studentProgressJourney: `${apiURLOrigin}/oh_student_progress/journey/`,
  progressReports: `${apiURLOrigin}/oh_student_progress/reports/`,
  subscription: `${apiURLOrigin}/oh_student_progress/subscription/`,
  // Twice-a-week cutover: same FSM actions, keyed by enrolment instead of
  // journey. `subscription` (legacy, journey-keyed) stays until Phase D.
  enrolmentSubscription: `${apiURLOrigin}/oh_classes/enrolment/`,
  paymentInvoice: `${apiURLOrigin}/oh_payments/invoice/alfred`,
  // Feature 3 — mid-admission daycare-billing toggle endpoint. Callers
  // append `<invoice_uuid>/switch_billing_mode/`.
  invoiceSwitchBillingMode: `${apiURLOrigin}/oh_payments/invoice`,
  coinTransaction: `${apiURLOrigin}/oh_payments/coin_transaction`,
  superprovider: `${apiURLOrigin}/oh_users/superprovider`,
  interactions: `${apiURLOrigin}/crm/interaction`
}

export const validateUpdateRequest = (data) => {
  if (isEmpty(data.uuid)) {
    throw new Error('uuid is missing / invalid')
  }
}

export const validateDeleteRequest = (ids) => {
  if (!(Array.isArray(ids) && !isEmpty(ids))) {
    throw new Error('ids are missing / invalid')
  }
}

export const generateOtp = async (data) => {
  try {
    const response = await post(`${apiEndpoints.auth}/generate_otp/`, {
      phone_number: data.phone_number
    })

    return response.data
  } catch (error) {
    sentryLogger(error, 'error in generate otp')
    throw error
  }
}

export const getIndianStates = async () => {
  try {
    const response = await get(`${apiEndpoints.constants}?name=states`)

    return response.data
  } catch (error) {
    sentryLogger(error, 'error in fetching Indian states')
    throw error
  }
}

export const fetchSchools = async ({ url, queries } = {}) => {
  const requestUrl = !isEmpty(url)
    ? url
    : makeRequestUrl(
        `${apiEndpoints.googlePlaces}/textsearch/?type=school&location=lat,long`,
        queries
      )

  try {
    const response = await get(requestUrl)

    return {
      ...response.data,
      results: [
        ...response.data.results.map((item) => ({
          ...item,
          label: isEmpty(item.area) ? item.name : `${item.name} | ${item.area}`
        }))
      ]
    }
  } catch (error) {
    sentryLogger(error, 'error in fetching schools')
    throw error
  }
}

export const fetchPreschoolAcademicYears = async ({ url, queries } = {}) => {
  const requestUrl = !isEmpty(url)
    ? url
    : makeRequestUrl(apiEndpoints.preschoolAcademicYears, {
        ...modifyQueries(queries)
      })

  try {
    const response = await get(requestUrl)
    return response.data
  } catch (error) {
    sentryLogger(error, ' error in fetching preschool academic years')
    throw error
  }
}

export const createSchool = async (data) => {
  try {
    const response = await post(`${apiEndpoints.common}/commons/school`, {
      name: data.name,
      locality: data.locality
    })

    return response.data
  } catch (error) {
    sentryLogger(error, 'error in creating board')
    throw error
  }
}

export const getConfigValue = async (queries) => {
  try {
    const requestUrl = makeRequestUrl(apiEndpoints.configApp, queries)
    const response = await get(requestUrl)
    return response.data.results[0].value
  } catch (error) {
    sentryLogger(error, 'error in fetching config value')
    throw error
  }
}
